<template>
  <div
    class="relative z-0 flex grow scrollbar-thin flex-col justify-between bg-primary-800 xl:overflow-y-auto"
  >
    <div>
      <div
        class="sticky top-0 z-10 bg-primary-800 py-4"
        :class="reducedSidebar ? 'xl:px-2' : 'xl:px-4'"
      >
        <alert
          v-if="
            !loadingOnboarding &&
            !vendor.live_mode &&
            vendor.onboarding_completed
          "
          data-cy="live-mode-alert"
          :variant="AlertType.WARNING"
        >
          <p class="font-semibold">{{ $t('onboarding.liveModeTitle') }}</p>
          <p class="my-1">{{ $t('onboarding.liveModeHelpText') }}</p>
          <a class="flex gap-2">
            <span
              class="underline-green-900 cursor-pointer font-bold underline"
              @click="enableLiveMode()"
              >{{ $t('onboarding.liveModeButton') }}</span
            >
            <loading-indicator
              v-if="savingLiveMode"
              :inline="true"
              :small="true"
            ></loading-indicator>
          </a>
        </alert>

        <alert
          v-if="!loadingOnboarding && !vendor.live_mode && onboardingStarted"
          data-cy="live-mode-alert"
          :variant="AlertType.INFO"
        >
          <p class="font-semibold">{{ $t('onboarding.setupTitle') }}</p>
          <p class="my-1">{{ $t('onboarding.helpText') }}</p>
        </alert>

        <router-link
          v-if="isStaffOrMore && vendor.live_mode"
          :to="{ name: 'AddBooking' }"
          class="flex h-12 items-center rounded-md bg-primary-100 font-bold text-primary-900 hover:bg-primary-50 hover:no-underline"
          :class="[
            reducedSidebar
              ? 'w-12 justify-center'
              : 'justify-between gap-4 px-4',
          ]"
        >
          <div class="inline-flex w-8 flex-none items-center justify-center">
            <i class="far fa-lg fa-plus"></i>
          </div>
          <sidebar-label>{{ $t('booking.titleAdd') }}</sidebar-label>
        </router-link>
      </div>

      <div class="xl:pb-4" :class="reducedSidebar ? 'xl:px-2' : 'xl:px-4'">
        <!-- Agendas -->

        <sidebar-title>
          {{ $t('sidebar.calendar') }}
        </sidebar-title>

        <sidebar-item
          v-if="isStaffOrMore"
          :title="$t('sidebar.employees')"
          :to="{
            name: 'Calendar',
            query: { viewType: CalendarViewType.EMPLOYEE },
          }"
          icon="user-group"
          calendar
        />

        <sidebar-item
          v-if="isStaffOrMore && workplacesCount > 0"
          :title="$t('sidebar.workplaces', 2)"
          :to="{
            name: 'Calendar',
            query: { viewType: CalendarViewType.WORKPLACE },
          }"
          icon="door-open"
          calendar
        />

        <sidebar-item
          v-if="isStaffOrMore && machinesCount > 0"
          :title="$t('sidebar.resources', 2)"
          :to="{
            name: 'Calendar',
            query: { viewType: CalendarViewType.MACHINE },
          }"
          icon="suitcase"
          calendar
        />

        <!-- Clients & Bons cadeaux -->

        <sidebar-title>
          {{ $t('sidebar.customers') }}
        </sidebar-title>

        <sidebar-item
          v-if="isStaffOrMore"
          :title="$t('sidebar.customers')"
          :to="{ name: 'Customers' }"
          icon="users"
        />

        <sidebar-item
          v-if="isStaffOrMore"
          :title="$t('sidebar.giftCards')"
          :to="{ name: 'Vouchers' }"
          icon="gift"
        />

        <div v-if="isAdmin">
          <!-- Rapport -->

          <sidebar-title>
            {{ $t('sidebar.reports') }}
          </sidebar-title>

          <sidebar-item
            v-if="isAdmin"
            :title="$t('sidebar.cashClosing')"
            :to="{ name: 'CashClosing' }"
            icon="cash-register"
          />

          <sidebar-item
            v-if="isAdmin"
            :title="$t('sidebar.stats')"
            :to="{ name: 'Stats' }"
            icon="chart-bar"
          />

          <sidebar-item
            v-if="isAdmin"
            :title="$t('sidebar.vendorBookings')"
            :to="{ name: 'VendorBookings' }"
            icon="calendar-check"
          />
        </div>

        <!-- Plannification -->

        <sidebar-title v-if="isAdmin || canEditTimetables">
          {{ $t('sidebar.planning') }}
        </sidebar-title>

        <sidebar-item
          v-if="isAdmin || canEditTimetables"
          :title="$t('sidebar.schedules')"
          :to="{
            name: 'Timetables',
            query: { notification: COLLECTION.TIMETABLES },
          }"
          icon="clock"
          :collection="COLLECTION.TIMETABLES"
        />

        <sidebar-item
          v-if="isAdmin"
          :title="$t('sidebar.closedDates')"
          :to="{
            name: 'ClosedDates',
            query: { notification: COLLECTION.CLOSED_DATES },
          }"
          icon="store-alt-slash"
          :collection="COLLECTION.CLOSED_DATES"
        />

        <!-- Configuration -->

        <sidebar-title v-if="isAdmin">
          {{ $t('sidebar.config') }}
        </sidebar-title>

        <sidebar-item
          v-if="isAdmin"
          :title="$t('sidebar.employees')"
          :to="{
            name: 'Employees',
            query: { notification: COLLECTION.EMPLOYEES },
          }"
          icon="user-friends"
          :collection="COLLECTION.EMPLOYEES"
        />

        <sidebar-item
          v-if="isAdmin"
          :title="$t('sidebar.services')"
          :to="{
            name: 'Services',
            query: { notification: COLLECTION.SERVICES },
          }"
          icon="concierge-bell"
          :collection="COLLECTION.SERVICES"
        />

        <sidebar-item
          v-if="isAdmin"
          :title="$t('sidebar.workplaces', 2)"
          :to="{
            name: 'Workplaces',
            query: { notification: COLLECTION.WORKPLACES },
          }"
          icon="door-open"
          :collection="COLLECTION.WORKPLACES"
        />

        <sidebar-item
          v-if="isAdmin"
          :title="$t('sidebar.sharedResources', 2)"
          :to="{
            name: 'Machines',
            query: { notification: COLLECTION.MACHINES },
          }"
          icon="briefcase"
          :collection="COLLECTION.MACHINES"
        />

        <sidebar-item
          v-if="isAdmin"
          :title="$t('sidebar.locations')"
          :to="{
            name: 'Locations',
            query: { notification: COLLECTION.LOCATIONS },
          }"
          icon="building"
          :collection="COLLECTION.LOCATIONS"
        />

        <sidebar-item
          v-if="isAdmin"
          :title="$t('navbar.settings')"
          :to="{ name: 'VendorLegalInformationForm' }"
          icon="sliders-v"
        />
      </div>
    </div>

    <sidebar-footer />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Collection } from '@/store/common/collections';
import { AlertType, useUiStore } from '@/store/ui-store';
import { useUsersStore } from '@/store/users-store';
import { useRoles } from '@/composables/roles';
import { CalendarResourceType } from '@/store/calendar-store';
import SidebarItem from '@/components/shared/SidebarItem.vue';
import SidebarTitle from '@/components/shared/SidebarTitle.vue';
import SidebarLabel from '@/components/shared/SidebarLabel.vue';
import SidebarFooter from '@/components/shared/SidebarFooter.vue';
import { useMachinesStore } from '@/store/machines-store';
import { useWorkplacesStore } from '@/store/workplaces-store';
import Alert from '@/components/shared/Alert.vue';
import { useVendorsStore } from '@/store/vendors-store';
import { VendorSettingsPage } from '@/model/vendor-settings-page';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import { useOnboarding } from '@/composables/onboarding';
import i18n from '@/config/i18n';

const router = useRouter();
const uiStore = useUiStore();
const usersStore = useUsersStore();
const workplacesStore = useWorkplacesStore();
const machinesStore = useMachinesStore();
const vendorsStore = useVendorsStore();

const { loadingOnboarding, onboardingStarted } = useOnboarding();

const COLLECTION = Collection;
const CalendarViewType = CalendarResourceType;

const vendor = computed(() => vendorsStore.currentEntity);
const canEditTimetables = computed(
  () => usersStore.user.entity.employee?.can_set_timetable,
);
const reducedSidebar = computed(() => uiStore.reducedSidebar);
const workplacesCount = computed(() => workplacesStore.entities.length);
const machinesCount = computed(() => machinesStore.entities.length);
const savingLiveMode = computed(() => vendorsStore.saving);
const { isAdmin, isStaffOrMore } = useRoles();

async function enableLiveMode() {
  const changesSaved = await vendorsStore.edit(
    {
      ...vendor.value,
      live_mode: true,
    },
    VendorSettingsPage.PUBLIC,
    false,
  );
  if (changesSaved) {
    uiStore.alertSuccess(i18n.global.t('toast.liveModeEnabled'));
  }
}

function getNotificationsCount(collection: Collection) {
  return uiStore.getNotificationsCount(collection);
}

function clearNotifications(sideBarItem: Collection) {
  uiStore.clearNotificationsCount(sideBarItem);
}

onMounted(() => {
  router.afterEach((to) => {
    if (
      to.query.notification !== undefined &&
      getNotificationsCount(to.query.notification as Collection)
    ) {
      clearNotifications(to.query.notification as Collection);
    }
  });
});
</script>
